import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    dataMappingService,
    spreadsheetService,
    validateImportService,
    createCustomersRes,
    getCustomerGroupsRes,
    customersKeys,
    customerGroupKeys,
} from "core";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "components/ui/toast";

type Step = 0 | 1 | 2;
type MapItem = { available: string; selected: string; error?: boolean };
type SheetHeader = { value: string; taken: boolean };
type ErrorMsg = { name: string; shown: boolean };

export function useLeadsImport({ updateCustomerGroup }: any) {
    const qc = useQueryClient();
    // dialog/step
    const [isOpen, setIsOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState<Step>(0);

    // files
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const folderInputRef = useRef<HTMLInputElement | null>(null);

    // parsed (outside state to avoid big re-renders)
    const parsedHeadersRef = useRef<string[]>([]);
    const parsedRowsRef = useRef<any[]>([]);

    // mapping
    const [dataKeys, setDataKeys] = useState<MapItem[]>([]);
    const [sheetHeaders, setSheetHeaders] = useState<SheetHeader[]>([]);
    const [errorMsg, setErrorMsg] = useState<ErrorMsg[]>([]);

    // validation
    const [mappedRows, setMappedRows] = useState<any[]>([]);
    const [validated, setValidated] = useState<any[]>([]);
    const [hasErrors, setHasErrors] = useState<boolean>(false);

    // misc
    const [groupName, setGroupName] = useState("");
    const [isWorking, setIsWorking] = useState(false);
    const [toastMsg, setToastMsg] = useState("");

    const canNext = useMemo(() => {
        if (currentStep === 0) return selectedFiles.length > 0;
        if (currentStep === 1) return true;
        if (currentStep === 2) return !hasErrors && validated.length >= 0; // Finish enabled even if 0 rows after skip
        return false;
    }, [currentStep, selectedFiles, hasErrors, validated.length]);

    const resetAll = useCallback(() => {
        setIsOpen(false);
        setCurrentStep(0);

        setSelectedFiles([]);
        parsedHeadersRef.current = [];
        parsedRowsRef.current = [];

        setDataKeys([]);
        setSheetHeaders([]);
        setErrorMsg([]);

        setMappedRows([]);
        setValidated([]);
        setHasErrors(false);

        setGroupName("");
        setToastMsg("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (folderInputRef.current) folderInputRef.current.value = "";
    }, []);

    /** ---------- File picking ---------- */
    const onAddFiles = useCallback(() => fileInputRef.current?.click(), []);
    const onAddFolder = useCallback(() => {
        try {
            folderInputRef.current?.setAttribute("webkitdirectory", "");
            folderInputRef.current?.setAttribute("directory", "");
        } catch { }
        folderInputRef.current?.click();
    }, []);

    const handleParsed = useCallback((headers: string[], rows: any[], inferredGroup: string | null) => {
        // cache parsed
        parsedHeadersRef.current = headers || [];
        parsedRowsRef.current = rows || [];

        // build *fresh* mapping structures but DO NOT advance the step yet
        const { mapData, sheetHeaders, errorMsg } =
            dataMappingService.buildDefaultMapping(headers || []);

        setDataKeys(mapData);
        setSheetHeaders(sheetHeaders);
        setErrorMsg(errorMsg);

        setGroupName(inferredGroup || "Leads");
    }, []);

    const onFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const incoming = Array.from(e.target.files || []);
        if (!incoming.length) return;
        try {
            setIsWorking(true);
            setToastMsg("");

            setSelectedFiles((prev) => {
                const next = [...prev, ...incoming];
                spreadsheetService.readFiles(next)
                    .then((parsed) => handleParsed(parsed.headers, parsed.rows, parsed.groupName))
                    .catch((err: any) => {
                        console.error(err);
                        setToastMsg(err?.message || "Failed to read file. Make sure it's xlsx/xls/csv/ods.");
                    })
                    .finally(() => setIsWorking(false));
                return next;
            });
        } catch (err: any) {
            console.error(err);
            setToastMsg(err?.message || "Failed to read file. Make sure it's xlsx/xls/csv/ods.");
            setIsWorking(false);
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }, [handleParsed]);

    const onFolderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const allFiles = Array.from(e.target.files || []);
        if (!allFiles.length) return;
        const valid = allFiles.filter((f) => /\.(csv|xlsx|xls|ods)$/i.test(f.name));
        const toAdd = valid.length > 0 ? valid : allFiles.slice(0, 1);
        const pseudo = { target: { files: toAdd } } as unknown as React.ChangeEvent<HTMLInputElement>;
        onFileChange(pseudo);
        if (folderInputRef.current) folderInputRef.current.value = "";
    }, [onFileChange]);

    const removeFile = useCallback(async (idx: number) => {
        const next = [...selectedFiles];
        next.splice(idx, 1);
        setSelectedFiles(next);

        if (next.length === 0) {
            parsedHeadersRef.current = [];
            parsedRowsRef.current = [];
            setDataKeys([]);
            setSheetHeaders([]);
            setErrorMsg([]);
            setMappedRows([]);
        } else {
            try {
                setIsWorking(true);
                const parsed = await spreadsheetService.readFiles(next);
                handleParsed(parsed.headers, parsed.rows, parsed.groupName);
            } catch (err: any) {
                console.error(err);
                setToastMsg(err?.message || "Failed to re-read files.");
            } finally {
                setIsWorking(false);
            }
        }
    }, [selectedFiles, handleParsed]);

    /** ---------- Mapping ---------- */
    const onChangeMapping = useCallback(
        (rowIndex: number, newHeaderValue: string) => {
            const dIndex = sheetHeaders.findIndex((h) => h.value === newHeaderValue);
            const prevSelected = dataKeys[rowIndex]?.selected || "";

            // If this header is already taken by a different row, free it from
            // that row first ("steal" behaviour) so the user can re-assign freely.
            let workingDataKeys = dataKeys;
            let workingHeaders = sheetHeaders;
            const takenByRow = dataKeys.findIndex(
                (k, i) => i !== rowIndex && k.selected === newHeaderValue
            );
            if (takenByRow >= 0) {
                workingDataKeys = dataKeys.map((k, i) =>
                    i === takenByRow ? { ...k, selected: "" } : k
                );
                workingHeaders = sheetHeaders.map((h) =>
                    h.value === newHeaderValue ? { ...h, taken: false } : h
                );
            }

            const { data, sheetHeaders: sh2, next } =
                dataMappingService.changeAvailableValues(
                    { value: newHeaderValue },
                    dIndex,
                    rowIndex,
                    prevSelected,
                    0,
                    workingDataKeys,
                    workingHeaders,
                );

            setDataKeys(data);
            setSheetHeaders(sh2);

            if (next) {
                const mapped = dataMappingService.validateKeys(data, parsedRowsRef.current);
                setMappedRows(mapped);
            }
        },
        [dataKeys, sheetHeaders]
    );

    /** Footer NEXT logic */
    const goNext = useCallback(() => {
        if (currentStep === 0) {
            if (selectedFiles.length > 0) setCurrentStep(1);
            return;
        }

        if (currentStep === 1) {
            const checked = dataMappingService.checkDataMapping(dataKeys);
            setDataKeys(checked);

            const emailSelected =
                !!checked.find((i) => i.available === "Email")?.selected;

            setErrorMsg((prev) =>
                (prev || []).map((m) =>
                    m.name.toLowerCase().includes("email must be selected")
                        ? { ...m, shown: !emailSelected }
                        : { ...m, shown: false }
                )
            );

            if (!emailSelected) return;
            const mapped = dataMappingService.validateKeys(checked, parsedRowsRef.current);
            setMappedRows(mapped);
            setCurrentStep(2);
            return;
        }
    }, [currentStep, selectedFiles.length, dataKeys]);

    /** ---------- Validation ---------- */
    const validateRows = useCallback(() => {
        const result = validateImportService.validationCheck(mappedRows);
        setValidated(result.rows);
        setHasErrors(result.check);
    }, [mappedRows]);

    useEffect(() => {
        if (currentStep === 2) validateRows();
    }, [currentStep, validateRows]);

    const skipRow = useCallback((idx: number) => {
        setValidated((prev) => validateImportService.skipRow(prev, idx));
        setHasErrors((_prev) => {
            const maybe = validateImportService.skipRow(validated, idx);
            return maybe.some((r: any) => r.emailError);
        });
    }, [validated]);

    const skipAll = useCallback(() => {
        setValidated((prev) => validateImportService.skipAll(prev));
        setHasErrors(false);
    }, []);

    /** ---------- Finish ---------- */
    const onFinishImport = useCallback(async () => {
        if (!groupName) {
            setToastMsg("Please select a file first.");
            return;
        }

        const customers = validated.map((row: any) => ({
            firstName: row["Name"] || null,
            lastName: row["Last Name"] || null,
            email: row["Email"] || null,
            phoneNumber: row["Phone"] || null,
            whatsappNumber: row["Whatsapp Number"] || null,
            messengerId: row["Messenger"] || null,
        }));

        setIsWorking(true);
        try {
            for (let i = 0; i < customers.length; i += 50) {
                const portion = customers.slice(i, i + 50);
                await createCustomersRes([{ groupName, customers: portion }]);
            }
            await Promise.all([
                qc.invalidateQueries({ queryKey: customersKeys.root }),
                qc.invalidateQueries({ queryKey: customerGroupKeys.root }),
            ]);
            await updateCustomerGroup();

            toast.success("Leads imported successfully.");
            await getCustomerGroupsRes();
            resetAll();
        } catch (e) {
            console.error(e);
            toast.error("Something went wrong!");
        } finally {
            setIsWorking(false);
        }
    }, [groupName, validated, qc, updateCustomerGroup, resetAll]);

    return {
        // dialog/step
        isOpen, setIsOpen,
        currentStep, setCurrentStep,
        goNext,

        // files
        selectedFiles,
        fileInputRef,
        folderInputRef,
        onAddFiles,
        onAddFolder,
        onFileChange,
        onFolderChange,
        removeFile,

        // mapping
        dataKeys,
        sheetHeaders,
        errorMsg,
        onChangeMapping,

        // validation
        mappedRows,
        validated,
        hasErrors,
        skipRow,
        skipAll,

        // misc
        groupName,
        isWorking,
        toastMsg, setToastMsg,
        canNext,

        // finish
        onFinishImport,

        // reset
        resetAll,
    };
}
